/**
 * brandImportTemplate: the BRAND workbook the operator starts from.
 * ───────────────────────────────────────────────────────────────────────────
 * The layout (READ ME, Column Guide, the two data sheets, the hint row above
 * each header, the self-filling formula fill-down, the Lists sheet and the
 * download plumbing) is catalogueImportTemplate.js, shared with every other
 * vertical. The columns come from brandImportColumns.js, the same list
 * brandImport.js reads headers with, so the template and the parser cannot
 * disagree. What is left here is the brand's own prose, its own reference
 * lists and its own example row.
 *
 * Read by:
 *
 *   1. `scripts/build_brand_import_template.mjs`, which writes the static blank
 *      preset to public/templates/ at build time.
 *   2. The "Download template" button in BrandImportModal, which builds a
 *      workbook in the browser pre-filled with THIS brand's spec fields and
 *      section titles.
 *
 * The layout follows the China Town workbook in Drive, the blueprint for every
 * brand sheet since 26 Sep 2026: Title Case headers, a one-line hint above
 * each, a Column Guide sheet, a Status column, and columns that fill
 * themselves in (To Do, Image Source, the section counts). The `xlsx` build
 * used here writes no colours; the blueprint in Drive carries the styled
 * version with its colour key.
 *
 * DELIBERATELY BLANK. The single example row carries the marker string the
 * parser drops, and no worked-example sheet ships at all: sheet picking is by
 * row count, so a filled example could outvote the operator's own rows.
 */
import * as XLSX from "xlsx";
import {
  NAMES,
  PRODUCTS_SHEET,
  PRODUCT_COLUMNS,
  PRODUCT_FORMULAS,
  ROLE,
  ROLE_LABEL,
  SECTIONS_SHEET,
  SECTION_COLUMNS,
  SECTION_FORMULAS,
  STATUS,
  columnLetters,
  productRange,
  specHeader,
} from "./brandImportColumns.js";
import { PAYMENT_METHODS } from "./brandImport.js";
import { ORDER_FLOWS } from "./brandImport.js";
import {
  EXAMPLE_NOTE,
  FIRST_DATA_ROW,
  GUIDE_SHEET,
  LISTS_SHEET,
  PRODUCT_FORMULA_ROWS,
  README_SHEET,
  SECTION_FORMULA_ROWS,
  SHARED_LIST_ROWS,
  YES_NO_LIST_ROWS,
  buildGuideRows,
  dataSheet,
  downloadWorkbook,
  templateFileNameFor,
  textSheet,
  PHOTO_README_ROWS,
  STAND_IN_NOTE,
} from "./catalogueImportTemplate.js";

/** Dropped by the parser. Re-exported so the old import path still resolves. */
export { EXAMPLE_NOTE } from "./catalogueImportTemplate.js";

const README = (brandName) => {
  const rows = [
    [`Lusuku bulk product import${brandName ? `: ${brandName}` : ""}`],
    [],
    ["Fill in the Sections and Products sheets, then upload this file on the brand's Product Management page."],
    [],
    ["HOW IT WORKS"],
    ["1.", `Put every product on the '${PRODUCTS_SHEET}' sheet, one row each. Section and Name are required.`],
    ["2.", "The To Do column says what each product still needs before it can go live. It updates as you type."],
    ["3.", "Price on Request and Image Source fill themselves in: entering a price turns Price on Request to No, and naming a real photo clears Placeholder."],
    ["4.", "Filter the Status column to see Active or Deactivated products."],
    ["5.", "Upload the workbook and the photo files together. Photos are matched to Image File, ignoring capitalisation."],
    ["6.", "Nothing is written until you have seen the review table and pressed Import."],
    ["7.", `The '${GUIDE_SHEET}' sheet explains every column.`],
    [],
    ["SETTINGS"],
  ];
  const standInRow = rows.length + 1; // 1-based row of the setting below
  rows.push(["Stand-in cover file", ""]);
  rows.push(["Type the file name of the brand's stand-in cover photo, if it has one. Products using it are marked Placeholder and show 'Needs photo' on Lusuku."]);
  rows.push([STAND_IN_NOTE]);
  rows.push(
    [],
    ...PHOTO_README_ROWS,
    [],
    ["THE RULES THAT BITE"],
    ["·", "Every product needs a cover image. There is no way to create one without a photo."],
    ["·", "Price (UGX) is the GROSS price a customer pays. Never enter your net price; Lusuku calculates commission from the gross."],
    ["·", "Rows marked Out of Stock are skipped, not imported as zero-stock products."],
    ["·", "Re-running the same workbook is safe. Anything already on this brand is skipped, matched on Barcode."],
    ["·", "Names may repeat inside a section when the Barcodes differ."],
    ["·", "Imports land DEACTIVATED by default. Activate products once prices, warranties and photos are confirmed."],
    ["·", "Delete the example row before importing. It is skipped anyway: it carries a marker in its notes."],
    [],
    ["YOUR OWN PRODUCT ATTRIBUTES"],
    [`Add a column for each one, named ${specHeader("model")}, ${specHeader("warranty")} and so on.`],
    ["The field keys come from this brand's spec template, set on the brand's own settings page."],
    ["A spec column whose key is not in that template still imports, but is flagged in the review."],
    [],
    ["WHAT IS NOT HERE, AND WHY"],
    ["·", "Commission, vendor net price and country have no columns. Lusuku calculates all three."],
    ["·", "There is no 'was' or RRP price column. Lusuku does not yet display a struck-through price."]
  );
  return { rows, standInRef: `'${README_SHEET}'!$B$${standInRow}` };
};

const GUIDE = (specFieldKeys) =>
  buildGuideRows(
    SECTION_COLUMNS,
    PRODUCT_COLUMNS,
    specFieldKeys.map((k) => [
      PRODUCTS_SHEET,
      specHeader(k),
      ROLE_LABEL[ROLE.OPTIONAL],
      `This brand's "${k}" attribute.`,
    ])
  );

const LISTS = (specFieldKeys, categoryNames, unitNames) => {
  const rows = [
    ...SHARED_LIST_ROWS(PAYMENT_METHODS),
    ["Order Flow"],
    ...ORDER_FLOWS.map((f) => [f]),
    [],
    ...YES_NO_LIST_ROWS,
  ];
  rows.push(["Spec columns for this brand"]);
  if (specFieldKeys.length) {
    for (const key of specFieldKeys) rows.push([specHeader(key)]);
  } else {
    rows.push(["This brand has no spec template set up yet."]);
    rows.push(["Add fields on the brand's settings page, then download this template again."]);
  }
  rows.push([]);
  rows.push(["Platform Categories"]);
  if (categoryNames.length) for (const c of categoryNames) rows.push([c]);
  else rows.push(["Loaded from Lusuku when you download this from a brand page."]);
  rows.push([]);
  rows.push(["Unit"]);
  if (unitNames.length) for (const u of unitNames) rows.push([u]);
  else rows.push(["Loaded from Lusuku when you download this from a brand page."]);
  return rows;
};

/**
 * Build the workbook.
 *
 * Called with no arguments it produces the generic blank preset. Called from a
 * brand's page it produces the SAME file with that brand's spec columns
 * already named and its existing sections already listed, which is the
 * difference between "here is a spreadsheet" and "here is your spreadsheet".
 *
 * @param {object}   opts
 * @param {string}   [opts.brandName]
 * @param {string[]} [opts.specFieldKeys]  brand.productSpecTemplate.fields[].fieldKey
 * @param {string[]} [opts.sectionTitles]  sections the brand already has
 * @param {string[]} [opts.categoryNames]  live Lusuku category names
 * @param {string[]} [opts.unitNames]      live Lusuku unit names
 * @returns an xlsx workbook object
 */
export const buildTemplateWorkbook = ({
  brandName = "",
  specFieldKeys = [],
  sectionTitles = [],
  categoryNames = [],
  unitNames = [],
} = {}) => {
  const wb = XLSX.utils.book_new();

  const readme = README(brandName);
  XLSX.utils.book_append_sheet(wb, textSheet(readme.rows), README_SHEET);

  XLSX.utils.book_append_sheet(
    wb,
    textSheet(GUIDE(specFieldKeys), [{ wch: 14 }, { wch: 24 }, { wch: 16 }, { wch: 90 }]),
    GUIDE_SHEET
  );

  // ── 1. Sections ──────────────────────────────────────────────────────────
  const productRowsEnd = FIRST_DATA_ROW + PRODUCT_FORMULA_ROWS - 1;
  const P = productRange(FIRST_DATA_ROW, productRowsEnd);
  const S = columnLetters(SECTION_COLUMNS);
  const sectionRows = sectionTitles.length
    ? // The brand's existing sections, so an operator adding to a live
      // catalogue spells them the same way and the importer reuses them
      // instead of creating a near-duplicate.
      sectionTitles.map((title, i) => ({
        section_order: i + 1,
        section_title: title,
        notes: "Already on this brand",
      }))
    : [
        {
          section_order: 1,
          section_title: "Example Section",
          section_description: "What belongs in it",
          notes: EXAMPLE_NOTE,
        },
      ];
  XLSX.utils.book_append_sheet(
    wb,
    dataSheet(
      SECTION_COLUMNS,
      [],
      sectionRows,
      (key, r) => SECTION_FORMULAS[key]?.(S, r, P),
      SECTION_FORMULA_ROWS
    ),
    SECTIONS_SHEET
  );

  // ── 2. Products ──────────────────────────────────────────────────────────
  const L = columnLetters(PRODUCT_COLUMNS);
  const specColumns = specFieldKeys.map((k) => ({
    key: `spec:${k}`,
    label: specHeader(k),
    hint: `This brand's "${k}" attribute.`,
  }));
  // The example row is keyed by field key so it cannot drift out of column
  // order when a column is inserted above.
  const example = {
    row_id: 1,
    section_title: sectionTitles[0] || "Example Section",
    name: "Example Product 55 inch",
    item_code: "EX-01",
    sku_code: "EXAMPLE-001",
    enabled: STATUS.DEACTIVATED,
    description: "One or two lines a shopper would read.",
    price_ugx: 1250000,
    availability: "In Stock",
    count_in_stock: 4,
    image_filename: "example-product.jpg",
    min_order_qty: 1,
    notes: EXAMPLE_NOTE,
  };
  XLSX.utils.book_append_sheet(
    wb,
    dataSheet(
      PRODUCT_COLUMNS,
      specColumns,
      [example],
      (key, r) => PRODUCT_FORMULAS[key]?.(L, r),
      PRODUCT_FORMULA_ROWS
    ),
    PRODUCTS_SHEET
  );

  XLSX.utils.book_append_sheet(
    wb,
    textSheet(LISTS(specFieldKeys, categoryNames, unitNames)),
    LISTS_SHEET
  );

  // The formulas read the stand-in cover file name from the READ ME.
  wb.Workbook = {
    ...(wb.Workbook || {}),
    Names: [{ Name: NAMES.STAND_IN_COVER, Ref: readme.standInRef }],
  };

  return wb;
};

/** Filename for a download. Brand-specific when we know the brand. */
export const templateFileName = (brandName) =>
  templateFileNameFor(brandName, "Lusuku_Brand_Import_Template.xlsx");

/** Browser-side download. No-op outside a browser. */
export const downloadTemplateWorkbook = (opts = {}) => {
  downloadWorkbook(buildTemplateWorkbook(opts), templateFileName(opts.brandName));
};

export default buildTemplateWorkbook;
